import React, { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, InfoBlock, Select } from '../../../components'
import { staffObjectStorageService } from '../../../api'
import { StaffBucket } from '../../../api/staffObjectStorageService'
import { useUi } from '../../../context/ui.context'
import { timeConverterMMDDYYHHMM } from '../../../utils/dates'
import { formatFileSize } from '../../../utils/sizes'
import { Card, CardTitle, Cards, Content, FormActions } from './styles'
import { displayValue, getObjectStorageError } from './utils'

type Props = {
  bucket: StaffBucket
  userListPath?: string
  onUpdate: () => void
}

const BucketOverview: FC<Props> = ({ bucket, userListPath, onUpdate }) => {
  const [t] = useTranslation()
  const { viewAlert } = useUi()
  const [access, setAccess] = useState(bucket.access)
  const [loading, setLoading] = useState(false)

  const saveAccess = async () => {
    try {
      setLoading(true)
      await staffObjectStorageService.updateBucket(bucket.id, access)
      viewAlert({ severity: 'information', message: t('BUCKET_VISIBILITY_UPDATED') })
      onUpdate()
    } catch (error) {
      viewAlert({
        severity: 'error',
        message: getObjectStorageError(error, t('ERROR_OCCURRED')),
      })
    } finally {
      setLoading(false)
    }
  }

  const bucketInfo = [
    { id: '1', title: t('ID'), value: bucket.id, copy: true },
    { id: '2', title: t('NAME'), value: bucket.name, copy: true },
    { id: '3', title: t('VISIBILITY'), value: bucket.access },
    { id: '4', title: t('OBJECTS'), value: bucket.num_objects },
    { id: '5', title: t('SIZE'), value: formatFileSize(bucket.usage) },
    { id: '6', title: t('CREATED_AT'), value: timeConverterMMDDYYHHMM(bucket.created_at) },
    { id: '7', title: t('UPDATED_AT'), value: timeConverterMMDDYYHHMM(bucket.updated_at) },
  ]

  const ownerInfo = [
    {
      id: '1',
      title: t('OBJECT_STORAGE_USER'),
      value: displayValue(bucket.object_user.ceph_uid),
      href: userListPath ? `${userListPath}/${bucket.object_user.id}` : undefined,
    },
    { id: '2', title: t('USER_ID'), value: bucket.object_user.id, copy: true },
    { id: '3', title: t('DELETED'), value: bucket.object_user.deleted ? t('YES') : t('NO') },
    { id: '4', title: t('DISABLED'), value: bucket.object_user.disabled ? t('YES') : t('NO') },
    {
      id: '5',
      title: t('CLIENT'),
      value: bucket.client?.display || bucket.client?.name || 'N/A',
      href: bucket.client ? `/clients-users/clients/${bucket.client.id}` : undefined,
    },
    {
      id: '6',
      title: t('SERVICE'),
      value: bucket.service?.display_name || 'N/A',
      href: bucket.service ? `/billing/services/${bucket.service.id}` : undefined,
    },
    { id: '7', title: t('SERVICE_STATUS'), value: displayValue(bucket.service?.status) },
  ]

  const accessOptions = [
    { value: 'private', label: t('PRIVATE') },
    { value: 'public-read', label: 'public-read' },
    { value: 'public-read-write', label: 'public-read-write' },
    { value: 'authenticated-read', label: 'authenticated-read' },
  ]

  if (!accessOptions.some((option) => option.value === access)) {
    accessOptions.unshift({ value: access, label: access })
  }

  return (
    <Content>
      <Cards>
        <InfoBlock
          title={t('BUCKET_INFO')}
          data={bucketInfo}
          customStyles={{ width: '100%' }}
        />
        <InfoBlock
          title={t('OWNER_INFORMATION')}
          data={ownerInfo}
          customStyles={{ width: '100%' }}
        />
      </Cards>
      <Card>
        <CardTitle>{t('BUCKET_VISIBILITY')}</CardTitle>
        <Select
          title={t('VISIBILITY')}
          data={accessOptions}
          selectedValue={access}
          onChange={setAccess}
          customStyles={{ maxWidth: '545px' }}
        />
        <FormActions>
          <Button
            variant="primary"
            size="display"
            title={t('SAVE')}
            loading={loading}
            disabled={access === bucket.access}
            onClick={saveAccess}
          />
        </FormActions>
      </Card>
    </Content>
  )
}

export default BucketOverview
